import React, { useState, useEffect } from "react";
import {
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
  IconButton,
  Button,
  Box,
  Tooltip,
  Chip,
  CircularProgress
} from "@mui/material";
import DownloadIcon from "@mui/icons-material/Download";
import DeleteIcon from "@mui/icons-material/Delete";
import RefreshIcon from "@mui/icons-material/Refresh";
import useAxiosPrivate from "../../hooks/useAxiosPrivate";

const ExportManager = () => {
  console.log("ExportManager mounting...");
  const [exports, setExports] = useState([]);
  const [loading, setLoading] = useState(false);
  const axiosPrivate = useAxiosPrivate();

  const fetchExports = async () => {
    console.log("Fetching exports...");
    setLoading(true);
    try {
      const response = await axiosPrivate.get("/defect-inspection/v1/exports");
      console.log("Exports received:", response.data.exports);
      setExports(response.data.exports || []);
    } catch (err) {
      console.error("Failed to fetch exports:", err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchExports();
    // Optional: Set up polling while there are processing tasks
    const hasProcessing = exports.some(e => e.status === 'processing');
    if (hasProcessing) {
        const timer = setTimeout(fetchExports, 5000);
        return () => clearTimeout(timer);
    }
  }, [exports.length]);

  const handleDownload = async (task) => {
    try {
      setLoading(true);
      const response = await axiosPrivate.get(`/defect-inspection/v1/exports/${task.id}`);
      
      if (response.data && response.data.download_url) {
        // Trigger browser download by navigating to the signed URL
        // This avoids CORS issues with XHR/blob while remaining secure
        window.open(response.data.download_url, "_blank");
      }
    } catch (err) {
      console.error("Download failed:", err);
      // Optional: show error snackbar
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async (taskId) => {
    try {
      await axiosPrivate.delete(`/defect-inspection/v1/exports/${taskId}`);
      setExports(exports.filter((e) => e.id !== taskId));
    } catch (err) {
      console.error("Failed to delete export:", err);
    }
  };

  const getStatusChip = (status) => {
    switch (status) {
      case "completed":
        return <Chip label="Ready" color="success" size="small" />;
      case "processing":
        return <Chip label="Processing" color="primary" size="small" variant="outlined" icon={<CircularProgress size={14} />} />;
      case "failed":
        return <Chip label="Failed" color="error" size="small" />;
      default:
        return <Chip label={status} size="small" />;
    }
  };

  return (
    <Box sx={{ mt: 4 }}>
      <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mb: 2 }}>
        <Typography variant="h6" component="div" sx={{ color: "#49758d", fontWeight: "bold" }}>
          Recent Exports
        </Typography>
        <IconButton onClick={fetchExports} disabled={loading} color="primary">
          <RefreshIcon />
        </IconButton>
      </Box>

      <TableContainer component={Paper} elevation={2} sx={{ borderRadius: "12px", overflow: "hidden" }}>
        <Table sx={{ minWidth: 650 }}>
          <TableHead sx={{ backgroundColor: "#f6fbfc" }}>
            <TableRow>
              <TableCell sx={{ fontWeight: "bold" }}>File Name</TableCell>
              <TableCell sx={{ fontWeight: "bold" }}>Format</TableCell>
              <TableCell sx={{ fontWeight: "bold" }}>Date Created</TableCell>
              <TableCell sx={{ fontWeight: "bold" }}>Status</TableCell>
              <TableCell align="right" sx={{ fontWeight: "bold" }}>Actions</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {exports.length === 0 ? (
              <TableRow>
                <TableCell colSpan={5} align="center" sx={{ py: 3, color: "text.secondary" }}>
                  {loading ? <CircularProgress size={24} /> : "No recent exports found."}
                </TableCell>
              </TableRow>
            ) : (
              exports.map((row) => (
                <TableRow key={row.id} hover>
                  <TableCell>{row.filename}</TableCell>
                  <TableCell sx={{ textTransform: "uppercase" }}>{row.format}</TableCell>
                  <TableCell>{new Date(row.created_at).toLocaleString()}</TableCell>
                  <TableCell>{getStatusChip(row.status)}</TableCell>
                  <TableCell align="right">
                    <Tooltip title="Download">
                      <span>
                        <IconButton 
                          color="primary" 
                          onClick={() => handleDownload(row)}
                          disabled={row.status !== "completed"}
                        >
                          <DownloadIcon />
                        </IconButton>
                      </span>
                    </Tooltip>
                    <Tooltip title="Delete">
                      <IconButton color="error" onClick={() => handleDelete(row.id)}>
                        <DeleteIcon />
                      </IconButton>
                    </Tooltip>
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </TableContainer>
    </Box>
  );
};

export default ExportManager;
